import React, { useRef, useEffect, useState } from 'react';
import { ExerciseEngine } from '../services/exerciseEngine';
import { ExerciseType, RepContext, Results } from '../types';
import { RefreshCcw, Camera, AlertTriangle } from 'lucide-react';

interface CameraViewProps {
  exerciseType: ExerciseType;
  onUpdate: (ctx: RepContext) => void;
  isPaused?: boolean;
  className?: string;
}

export const CameraView: React.FC<CameraViewProps> = ({ exerciseType, onUpdate, isPaused = false, className = '' }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const engineRef = useRef<ExerciseEngine>(new ExerciseEngine(exerciseType));
  const requestRef = useRef<number>(0);
  
  const lastContextRef = useRef<string>(""); 

  const [loading, setLoading] = useState(true);
  const [statusMessage, setStatusMessage] = useState("Loading AI Core...");
  const [error, setError] = useState<string | null>(null);
  const [showTapToStart, setShowTapToStart] = useState(false);

  useEffect(() => {
    let pose: any = null;
    let active = true;
    let stream: MediaStream | null = null;
    let initTimer: any = null;

    const handleResize = () => {
        if (videoRef.current && canvasRef.current) {
            // Match canvas size to video size for accurate mapping
            const vWidth = videoRef.current.videoWidth;
            const vHeight = videoRef.current.videoHeight;
            if (vWidth && vHeight) {
                canvasRef.current.width = vWidth;
                canvasRef.current.height = vHeight;
            }
        }
    };
    window.addEventListener('resize', handleResize);

    const onResults = (results: Results) => {
      if (!active) return;
      if (loading) setLoading(false);
      if (showTapToStart) setShowTapToStart(false); // Hide button if frames are processing
      
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');

      if (!canvas || !ctx || !videoRef.current) return;

      // Sync Dimensions safely
      if (videoRef.current.videoWidth && (canvas.width !== videoRef.current.videoWidth || canvas.height !== videoRef.current.videoHeight)) {
          canvas.width = videoRef.current.videoWidth;
          canvas.height = videoRef.current.videoHeight;
      }

      ctx.save();
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw video
      ctx.drawImage(results.segmentationMask || videoRef.current, 0, 0, canvas.width, canvas.height);
      
      // Darken overlay for UI contrast
      ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      if (!isPaused && results.poseLandmarks) {
        if (window.drawConnectors && window.drawLandmarks) {
          window.drawConnectors(ctx, results.poseLandmarks, window.POSE_CONNECTIONS, {
            color: '#FFFFFF', 
            lineWidth: 3
          });
          window.drawLandmarks(ctx, results.poseLandmarks, {
            color: '#71717A', 
            lineWidth: 1,
            radius: 3
          });
        }

        const newContext = engineRef.current.analyze(results.poseLandmarks);
        
        const contextHash = `${newContext.count}-${newContext.state}-${newContext.feedback}`;
        if (contextHash !== lastContextRef.current) {
            lastContextRef.current = contextHash;
            onUpdate(newContext);
        }
      } else if (isPaused) {
        ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      ctx.restore();
    };

    // Polling function to wait for MediaPipe scripts to load
    const waitForMediaPipe = async (): Promise<void> => {
        return new Promise((resolve, reject) => {
            const start = Date.now();
            const check = () => {
                if (window.Pose) {
                    resolve();
                } else if (Date.now() - start > 10000) { // 10 second timeout
                    reject(new Error("Network slow: AI modules timed out."));
                } else {
                    setTimeout(check, 200);
                }
            };
            check();
        });
    };

    const initMediaPipe = async () => {
      try {
        setStatusMessage("Loading AI Modules...");
        await waitForMediaPipe();

        if (!window.Pose) throw new Error("MediaPipe modules failed to load.");

        pose = new window.Pose({
          locateFile: (file: string) => `https://cdn.jsdelivr.net/npm/@mediapipe/pose/${file}`,
        });

        pose.setOptions({
          modelComplexity: 0, // Lite model essential for Android performance
          smoothLandmarks: true,
          enableSegmentation: false, 
          minDetectionConfidence: 0.5,
          minTrackingConfidence: 0.5,
        });

        pose.onResults(onResults);

        if (navigator.mediaDevices && videoRef.current) {
          setStatusMessage("Accessing Camera...");
          
          // --- ANDROID OPTIMIZED CAMERA STRATEGY ---
          const getStream = async () => {
             // Attempt 1: Ideal for Performance (640x480)
             try {
                 return await navigator.mediaDevices.getUserMedia({
                     video: { 
                         facingMode: 'user',
                         width: { ideal: 640 },
                         height: { ideal: 480 },
                         frameRate: { ideal: 30 }
                     },
                     audio: false
                 });
             } catch (e) {
                 console.warn("Ideal resolution failed, retrying...");
             }

             // Attempt 2: Standard User Facing
             try {
                 return await navigator.mediaDevices.getUserMedia({
                     video: { facingMode: 'user' },
                     audio: false
                 });
             } catch (e) {
                 console.warn("Facing mode failed, using fallback...");
             }

             // Attempt 3: Absolute Fallback (Any Camera)
             return await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
          };

          try {
            stream = await getStream();
          } catch (err) {
             console.error("Camera Access Error:", err);
             throw new Error("Camera access denied. Check settings.");
          }
          
          if (!active || !videoRef.current) return;
          
          videoRef.current.srcObject = stream;
          
          // Fix for Android: Set playsInline explicitly via attribute helper if needed
          videoRef.current.setAttribute('playsinline', 'true');
          videoRef.current.setAttribute('webkit-playsinline', 'true');
          
          // Use onloadeddata instead of onloadedmetadata for Android reliability
          videoRef.current.onloadeddata = () => {
             if (!active || !videoRef.current) return;
             
             setStatusMessage("Starting...");
             
             const playPromise = videoRef.current.play();
             if (playPromise !== undefined) {
                 playPromise
                    .then(() => {
                        handleResize();
                        startProcessing();
                    })
                    .catch(error => {
                        console.warn("Autoplay prevented by browser:", error);
                        setShowTapToStart(true);
                        setLoading(false);
                    });
             }
          };
          
          // Safety timeout if video never loads
          initTimer = setTimeout(() => {
              if (loading && !showTapToStart && active) {
                  setShowTapToStart(true); // Assume autoplay block if it takes too long
                  setLoading(false);
              }
          }, 3000);
        }
      } catch (err: any) {
        console.error(err);
        setError(err.message || "Initialization failed.");
        setLoading(false);
      }
    };

    const startProcessing = () => {
        const processFrame = async () => {
            if (!active) return;
            if (videoRef.current && videoRef.current.readyState >= 2 && pose) {
                try {
                    await pose.send({ image: videoRef.current });
                } catch (e) {
                    // Silent fail for dropped frames
                }
            }
            requestRef.current = requestAnimationFrame(processFrame);
        };
        processFrame();
    };

    initMediaPipe();

    return () => {
      active = false;
      clearTimeout(initTimer);
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(requestRef.current);
      if (pose) pose.close();
      if (stream) {
          stream.getTracks().forEach(track => {
              track.stop();
          });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exerciseType]);

  const handleManualStart = () => {
      if (videoRef.current) {
          videoRef.current.play()
            .then(() => {
                setShowTapToStart(false);
                setLoading(true); // Temporary loading state while AI catches up
            })
            .catch(e => console.error("Manual start failed", e));
      }
  };

  return (
    <div className={`relative bg-black overflow-hidden ${className}`}>
      {/* Loading State */}
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center z-20 bg-black/90 backdrop-blur-sm">
          <div className="text-center px-6">
            <div className="w-12 h-12 border-t-2 border-b-2 border-white rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-white font-mono text-xs uppercase tracking-widest animate-pulse">{statusMessage}</p>
          </div>
        </div>
      )}

      {/* Tap To Start (Android Autoplay Block Fix) */}
      {showTapToStart && !error && (
         <div className="absolute inset-0 flex items-center justify-center z-30 bg-black/80 backdrop-blur-md">
            <button 
                onClick={handleManualStart}
                className="flex flex-col items-center justify-center gap-4 group"
            >
                <div className="w-20 h-20 rounded-full bg-white/10 border border-white/50 flex items-center justify-center group-hover:bg-white/20 group-hover:scale-105 transition-all">
                    <Camera className="w-8 h-8 text-white" />
                </div>
                <span className="text-white font-bold uppercase tracking-widest text-sm">Tap to Start Camera</span>
            </button>
         </div>
      )}
      
      {/* Error State */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center z-20 bg-surface p-6 text-center">
            <div className="max-w-xs flex flex-col items-center">
                <AlertTriangle className="w-10 h-10 text-red-500 mb-4" />
                <p className="text-white font-bold mb-2">Camera System Error</p>
                <p className="text-slate-400 text-xs font-mono mb-6">{error}</p>
                <button 
                    onClick={() => window.location.reload()}
                    className="flex items-center gap-2 px-6 py-3 bg-white text-black text-xs font-bold uppercase rounded-sm hover:bg-gray-200 transition-colors"
                >
                    <RefreshCcw className="w-3 h-3" />
                    Reboot System
                </button>
            </div>
        </div>
      )}

      <video 
        ref={videoRef} 
        className="absolute inset-0 w-full h-full opacity-0 pointer-events-none" 
        playsInline 
        webkit-playsinline="true"
        muted 
        autoPlay 
      />
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full object-cover -scale-x-100" />
      
      <div className="absolute inset-0 bg-[url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAADCAYAAABS3WWCAAAAE0lEQVQIW2NkQAKrVq36zwjjAwAoOQcLlp3fzgAAAABJRU5ErkJggg==')] opacity-10 pointer-events-none mix-blend-overlay"></div>
    </div>
  );
};
